import type { OrderCancelType } from '@/infrastructure/FulfillmentAdminClient';

/**
 * A packed web order still in the warehouse cancels in two steps (SSD-7676, decided 10/26): CS asks, FC unpacks the
 * parcel and confirms, and only then does the order cancel. The server has no "cancel requested" state, so the request is
 * an order hold whose reason carries what CS chose. A hold already stops the order being outbounded again, and every
 * screen that lists holds (Orders, View Orders, Arrival Sort) shows the reason as written, so it reads as a sentence.
 * This module is the one place that writes that sentence and reads it back.
 */

/** What CS chose, kept on the hold until FC confirms the cancel. */
export interface CancelRequest {
  cancelType: OrderCancelType;
  reason: string;
  /** Why a locked order is cancelled anyway, when CS was asked for it and it fits on the hold. */
  lockOverrideReason?: string;
}

const PREFIX = 'Cancel requested';
const LOCK_PART = ' · After the lock: ';
/** The server keeps up to 500 characters of a hold reason. */
export const HOLD_REASON_MAX = 500;

const TYPE_WORDS: Record<OrderCancelType, string> = {
  CHANGE_OF_MIND: "customer's own",
  COMPANY: 'our side',
};

/** Whose reason a cancel is, in the words the hold and the order's comments both use. */
export const cancelTypeWords = (cancelType: OrderCancelType): string => TYPE_WORDS[cancelType];

/**
 * The hold reason a cancel request is filed under, within the server's 500 characters. The reason is kept whole, except
 * that the lock separator inside CS's own words is written with a dash so the hold reads back the same; a lock reason
 * that does not fit is left off rather than cut, so FC is asked for it on the confirm (the comment keeps both whole).
 */
export const formatCancelRequestHold = (request: CancelRequest): string => {
  // The lock reason's separator inside CS's own words would split the reason in two on the way back, so it is spelled
  // with a dash there.
  const reason = request.reason.trim().split(LOCK_PART).join(' - After the lock: ');
  const head = `${PREFIX} (${TYPE_WORDS[request.cancelType]}): ${reason}`.slice(0, HOLD_REASON_MAX);
  const lock = request.lockOverrideReason?.trim();
  const withLock = lock ? `${head}${LOCK_PART}${lock}` : head;
  return withLock.length > HOLD_REASON_MAX ? head : withLock;
};

const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const PATTERN = new RegExp(
  `^${escape(PREFIX)} \\((${Object.values(TYPE_WORDS).map(escape).join('|')})\\): ([\\s\\S]+?)(?:${escape(LOCK_PART)}([\\s\\S]+))?$`,
);

/** What a hold reason says CS asked for, or null when the hold is not a cancel request (a customs hold, say). */
export const parseCancelRequestHold = (holdReason: string | null | undefined): CancelRequest | null => {
  if (!holdReason) {
    return null;
  }
  const match = PATTERN.exec(holdReason);
  if (!match) {
    return null;
  }
  const [, typeWords, reason, lock] = match;
  const cancelType: OrderCancelType = typeWords === TYPE_WORDS.CHANGE_OF_MIND ? 'CHANGE_OF_MIND' : 'COMPANY';
  return {
    cancelType,
    reason: reason!.trim(),
    ...(lock && lock.trim() !== '' && { lockOverrideReason: lock.trim() }),
  };
};
